import {createFileRoute} from '@tanstack/react-router';

import type {ClaimListScope} from '@/lib/list-scope';
import {parseIsoDay, parseNonEmpty, parsePage} from '@/lib/search-params';
import {ReimbursementListPage} from '@/modules/reimbursements/pages/ReimbursementListPage';
import {parseStatus} from '@/modules/reimbursements/lib/status-filter';
import type {ReimbursementStatusFilter} from '@/modules/reimbursements/lib/status-filter';

/**
 * The saved views. One question each: what state are these claims in.
 *
 * `mine` used to be a member and was the whole problem — it asked *whose*, from
 * inside the control that answers *what state*, so the two could not compose
 * and a manager's own submitted claim was unreachable (the only self-scoped
 * view was drafts). Whose-rows is `scope` below; what its tabs mean, and which
 * role sees which, lives in `lib/list-tabs.ts`.
 *
 * `paid` and `rejected` were members before that, with empty-state copy and no
 * `tabParams` case, so `?tab=paid` showed EVERY claim under a "no paid
 * reimbursements yet" empty state. Both are real views now — `paid` on its own,
 * `rejected` inside `finished` — carrying the status filter they always implied.
 */
export type ReimbursementsListTab =
  | 'all'
  // Claims sitting with YOU for a decision right now — not the same question as
  // `awaiting`, which is every submitted claim in scope.
  | 'needs_decision'
  | 'drafts'
  // Waiting on somebody else, from the requester's side: submitted, approved
  // and partially-approved all mean "not my move".
  | 'in_progress'
  | 'awaiting'
  | 'ready_to_pay'
  | 'paid'
  // Both refusals in one tab. The pills still tell an approver's rejection, the
  // payout desk's, and a withdrawal apart.
  | 'finished';

const TAB_VALUES: ReadonlyArray<ReimbursementsListTab> = [
  'all',
  'needs_decision',
  'drafts',
  'in_progress',
  'awaiting',
  'ready_to_pay',
  'paid',
  'finished',
];

/**
 * Whose rows the list shows — the question that used to share the tab row with
 * "what state are they in".
 *
 * Everyone / Mine for a holder of `reimbursements:view_all`, Mine / To approve for
 * an approver without it, Mine alone for anyone else -- `claimListScope`
 * decides which, and the page ignores a URL scope the viewer cannot have.
 */
export type ReimbursementsListScope = ClaimListScope;

const SCOPE_VALUES: ReadonlyArray<ReimbursementsListScope> = ['all', 'mine', 'approving'];

/**
 * URL state for the reimbursements index. Fields are optional in the
 * type — links from outside can navigate here without specifying search
 * params.
 *
 * `tab` and `scope` are deliberately left undefined rather than defaulted here:
 * both defaults depend on what the viewer may see, which this validator cannot
 * know. The page resolves them (`resolveTab` / the scope control) — see
 * `lib/list-tabs.ts` for why the landing tab is per-role.
 */
export interface ReimbursementsListSearch {
  tab?: ReimbursementsListTab;
  scope?: ReimbursementsListScope;
  status?: ReimbursementStatusFilter;
  /** A SITE id — the filter is labelled by the site's SQL Account code (DERP-233). */
  site?: string;
  /** Inclusive SUBMITTED-date bounds, ISO `yyyy-MM-dd` (MYT days on the API). */
  from?: string;
  to?: string;
  q?: string;
  page?: number;
}

function parseTab(value: unknown): ReimbursementsListTab | undefined {
  return TAB_VALUES.find((t) => t === value);
}

function parseScope(value: unknown): ReimbursementsListScope | undefined {
  return SCOPE_VALUES.find((s) => s === value);
}

const validateReimbursementsListSearch = (
  search: Record<string, unknown>,
): ReimbursementsListSearch => ({
  tab: parseTab(search.tab),
  scope: parseScope(search.scope),
  status: parseStatus(search.status),
  site: parseNonEmpty(search.site),
  from: parseIsoDay(search.from),
  to: parseIsoDay(search.to),
  q: parseNonEmpty(search.q),
  page: parsePage(search.page),
});

export const Route = createFileRoute('/_authenticated/reimbursements/')({
  validateSearch: validateReimbursementsListSearch,
  component: ReimbursementListPage,
});
